import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  Row,
  RowSelectionState,
  SortingState,
  useReactTable,
  OnChangeFn,
} from "@tanstack/react-table";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  type DragEndEvent,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AxDataTableFloatingDock } from "../ax/dataTable/AxDataTableFloatingDock";
import { selectColumn } from "../ax/dataTable/AxDataTableColumnHelpers";
import { cn } from "@/lib/utils";

interface DraggableRowProps<TData> {
  row: Row<TData>;
}

interface BaseDataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  rowCount: number;
  getRowId: (row: TData) => string;
  onReorder?: (newData: TData[]) => void;
  onDeleteRows?: (ids: any[]) => Promise<void>;
  deleting?: boolean;
  onClickSort?: (sorting: SortingState) => void;
  initialSorting?: SortingState;
  sortableColumns?: (keyof TData & string)[];
}

function DragHandleCell({ rowId }: { rowId: string }) {
  const { attributes, listeners } = useSortable({
    id: rowId,
  });

  return (
    <div {...attributes} {...listeners} className="cursor-move">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M4 4h2v2H4V4zm6 0h2v2h-2V4zM4 8h2v2H4V8zm6 0h2v2h-2V8zm-6 4h2v2H4v-2zm6 0h2v2h-2v-2z" />
      </svg>
    </div>
  );
}

function DraggableRow<TData>({ row }: DraggableRowProps<TData>) {
  const { transform, transition, setNodeRef, isDragging } = useSortable({
    id: row.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    position: "relative" as const,
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <TableRow 
      ref={setNodeRef} 
      style={style} 
      key={row.id} 
      data-state={row.getIsSelected() && "selected"}
      className="group transition-all duration-200 hover:bg-accent/10 hover:shadow-sm"
    >
      <TableCell className="w-[40px]">
        <DragHandleCell rowId={row.id} />
      </TableCell>
      {row.getVisibleCells().map((cell) => (
        <TableCell 
          key={cell.id}
          className="group-hover:text-accent-foreground/90 transition-colors duration-200"
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </TableRow>
  );
}

function RegularRow<TData>({ row }: { row: Row<TData> }) {
  return (
    <TableRow 
      key={row.id} 
      data-state={row.getIsSelected() && "selected"}
      className="group transition-all duration-200 hover:bg-accent/10 hover:shadow-sm"
    >
      {row.getVisibleCells().map((cell) => (
        <TableCell 
          key={cell.id}
          className="group-hover:text-accent-foreground/90 transition-colors duration-200"
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </TableRow>
  );
}

export function BaseDataTable<TData, TValue>({
  columns,
  data,
  rowCount,
  getRowId,
  onReorder,
  onDeleteRows,
  deleting,
  onClickSort,
  initialSorting = [],
  sortableColumns,
}: BaseDataTableProps<TData, TValue>) {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>(initialSorting);

  const dataIds = useMemo<UniqueIdentifier[]>(() => data.map(getRowId), [data, getRowId]);

  const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}), useSensor(KeyboardSensor, {}));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = dataIds.indexOf(active.id);
      const newIndex = dataIds.indexOf(over.id);
      const newData = arrayMove(data, oldIndex, newIndex);
      onReorder?.(newData);
    }
  };

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const onDeselectAll = () => setRowSelection({});

  const columnsMapped = onDeleteRows ? [selectColumn, ...columns] : columns;

  const handleSortingChange: OnChangeFn<SortingState> = (
    updaterOrValue: SortingState | ((old: SortingState) => SortingState)
  ) => {
    const updatedSorting = typeof updaterOrValue === "function" ? updaterOrValue(sorting) : updaterOrValue;

    setSorting(updatedSorting);
    onClickSort?.(updatedSorting);
  };

  const table = useReactTable({
    data,
    // @ts-ignore
    columns: columnsMapped,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    onSortingChange: onClickSort ? handleSortingChange : undefined,
    // Only use client-side sorting if onClickSort is not provided
    getSortedRowModel: undefined,
    manualSorting: Boolean(onClickSort),
    rowCount,
    state: {
      sorting,
      rowSelection: rowSelection,
    },
    getRowId,
    onRowSelectionChange: setRowSelection,
  });

  const deleteAndRefresh = async (ids: string[]) => {
    await onDeleteRows?.(ids);
    setRowSelection({});
  };

  const content = (
    <div className="rounded-lg border bg-card shadow-sm transition-all duration-300 hover:shadow-md">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="bg-muted/30 transition-colors duration-200">
              {onReorder && <TableHead className="w-[40px] bg-transparent" />}
              {headerGroup.headers.map((header) => {
                // @ts-ignore
                const accessorKey = header.column.columnDef.accessorKey;
                const isSortable = onClickSort && sortableColumns?.includes(accessorKey);
                return (
                  <TableHead
                    key={header.id}
                    className={cn(
                      "bg-transparent transition-colors duration-200",
                      isSortable && "cursor-pointer select-none hover:bg-accent/20"
                    )}
                    onClick={isSortable ? header.column.getToggleSortingHandler() : undefined}
                  >
                    {header.isPlaceholder ? null : (
                      <div className="flex items-center gap-1 font-medium">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {isSortable && (
                          <span className={cn(
                            "ml-1 transition-opacity duration-200",
                            !header.column.getIsSorted() && "opacity-0 group-hover:opacity-70"
                          )}>
                            {header.column.getIsSorted() === "asc" ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="h-4 w-4 transition-transform duration-200 hover:scale-110"
                              >
                                <path d="m5 15 7-7 7 7" />
                              </svg>
                            ) : header.column.getIsSorted() === "desc" ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="h-4 w-4 transition-transform duration-200 hover:scale-110"
                              >
                                <path d="m19 9-7 7-7-7" />
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="h-4 w-4 opacity-50 transition-all duration-200 hover:scale-110 hover:opacity-100"
                              >
                                <path d="m7 15 5 5 5-5" />
                                <path d="m7 9 5-5 5 5" />
                              </svg>
                            )}
                          </span>
                        )}
                      </div>
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table
              .getRowModel()
              .rows.map((row) =>
                onReorder ? <DraggableRow key={row.id} row={row} /> : <RegularRow key={row.id} row={row} />
              )
          ) : (
            <TableRow>
              <TableCell 
                colSpan={columnsMapped.length + 1} 
                className="h-32 text-center animate-in fade-in-50 duration-300"
              >
                <div className="flex flex-col items-center justify-center space-y-2 text-muted-foreground">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-12 w-12 opacity-20"
                  >
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                  <p className="text-sm">{t("dataTable.noResults")}</p>
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {onDeleteRows && (
        <AxDataTableFloatingDock
          onDeleteRows={deleteAndRefresh}
          rowSelection={rowSelection}
          onDeselectAll={onDeselectAll}
          deleting={deleting}
        />
      )}
    </div>
  );

  if (!onReorder) {
    return content;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={dataIds} strategy={verticalListSortingStrategy}>
        {content}
      </SortableContext>
    </DndContext>
  );
}
